import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";

type ConversationsContextValue = {
  isChatSelected: boolean;
  setIsChatSelected: (value: boolean) => void;
  isKeyboardOpen: boolean;
};

const ConversationsContext = createContext<ConversationsContextValue | undefined>(undefined);

export const useConversations = () => {
  const context = useContext(ConversationsContext);
  if (!context) {
    throw new Error("useConversations must be used within a ConversationsProvider");
  }
  return context;
};

export const ConversationsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isChatSelected, setIsChatSelected] = useState(false);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);

  const initialHeightRef = useRef(0);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;

    initialHeightRef.current = viewport.height;

    const updateVars = () => {
      const vvh = viewport.height;
      const vvTop = viewport.offsetTop;
      const diff = initialHeightRef.current - vvh;
      const keyboardOpen = diff > 150;

      setIsKeyboardOpen(keyboardOpen);

      // --vv-top: distância do topo do visualViewport ao topo do documento
      // --vv-height: altura real visível (encolhe com o teclado)
      // Usados pelo container de conversas para se ancorar exatamente ao viewport visível
      document.documentElement.style.setProperty("--vv-top", `${vvTop}px`);
      document.documentElement.style.setProperty("--vv-height", `${vvh}px`);
    };

    const handleOrientationChange = () => {
      // Aguarda o browser re-calcular as dimensões após a rotação
      setTimeout(() => {
        initialHeightRef.current = viewport.height;
        updateVars();
      }, 300);
    };

    updateVars();

    viewport.addEventListener("resize", updateVars);
    viewport.addEventListener("scroll", updateVars);
    window.addEventListener("orientationchange", handleOrientationChange);
    return () => {
      viewport.removeEventListener("resize", updateVars);
      viewport.removeEventListener("scroll", updateVars);
      window.removeEventListener("orientationchange", handleOrientationChange);
      document.documentElement.style.removeProperty("--vv-top");
      document.documentElement.style.removeProperty("--vv-height");
    };
  }, []);

  const value = useMemo<ConversationsContextValue>(
    () => ({ isChatSelected, setIsChatSelected, isKeyboardOpen }),
    [isChatSelected, isKeyboardOpen]
  );

  return (
    <ConversationsContext.Provider value={value}>
      {children}
    </ConversationsContext.Provider>
  );
};
